import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Filter, Copy, Ban, Download, Eye, QrCode, Upload } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const RelicCodesManager = () => {
  const [codes, setCodes] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCode, setSelectedCode] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  const [filters, setFilters] = useState({
    code: '',
    rarity: '',
    batch_id: '',
    status: '',
    page: 1,
    page_size: 50
  });
  
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    page_size: 50,
    total_pages: 0
  });

  useEffect(() => {
    fetchBatches();
  }, []);

  useEffect(() => {
    fetchCodes();
  }, [filters]);

  const fetchBatches = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/admin/relic/batches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBatches(response.data);
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const fetchCodes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await axios.get(`${API_URL}/api/admin/relic/codes?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCodes(response.data.codes);
      setPagination({
        total: response.data.total,
        page: response.data.page,
        page_size: response.data.page_size,
        total_pages: response.data.total_pages
      });
    } catch (error) {
      console.error('Error fetching codes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = async (codeId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/admin/relic/codes/${codeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedCode(response.data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error fetching code detail:', error);
      alert('Failed to load code details');
    }
  };

  const handleVoidCode = async (codeId) => {
    if (!window.confirm('Void this code? This action cannot be undone.')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(
        `${API_URL}/api/admin/relic/codes/${codeId}/void`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchCodes();
    } catch (error) {
      console.error('Error voiding code:', error);
      alert('Failed to void code');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value && key !== 'page' && key !== 'page_size') {
          params.append(key, value);
        }
      });
      
      // Create download link
      const url = `${API_URL}/api/admin/relic/codes/export/csv?${params}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `relic_codes_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export CSV');
    }
  };

  const handleImportCSV = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      alert('Please upload a CSV file');
      return;
    }
    
    setImporting(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${API_URL}/api/admin/relic/codes/import/csv`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setImportResult(response.data);
      
      // Refresh codes list
      fetchCodes();
      fetchBatches();
      
      // Reset file input
      event.target.value = '';
      
    } catch (error) {
      console.error('Error importing CSV:', error);
      setImportResult({
        success: false,
        error: error.response?.data?.detail || 'Failed to import CSV'
      });
    } finally {
      setImporting(false);
    }

  };

  const getStatusBadge = (status) => {
    const colors = {
      UNUSED: 'bg-gray-100 text-gray-800',
      PENDING: 'bg-yellow-100 text-yellow-800',
      REDEEMED: 'bg-green-100 text-green-800',
      VOID: 'bg-red-100 text-red-800'
    };
    return colors[status] || colors.UNUSED;
  };

  const getRarityBadge = (rarity) => {
    const colors = {
      COMMON: 'bg-gray-100 text-gray-700',
      UNCOMMON: 'bg-green-100 text-green-700',
      RARE: 'bg-blue-100 text-blue-700',
      LEGENDARY: 'bg-purple-100 text-purple-700'
    };
    return colors[rarity] || colors.COMMON;
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold">Relic Codes</h1>
          <div className="flex gap-2">
            {/* Import CSV Button */}
            <label className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 cursor-pointer">
              <Upload className="w-4 h-4" />
              <span>{importing ? 'Importing...' : 'Import CSV'}</span>
              <input
                type="file"
                accept=".csv"
                onChange={handleImportCSV}
                disabled={importing}
                className="hidden"
              />
            </label>
            
            {/* Export CSV Button */}
            <button
              onClick={handleExportCSV}
              className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
            >
              <Download className="w-4 h-4" />
              <span>Export CSV</span>
            </button>
          </div>
        </div>
        
        {/* Import Result Message */}
        {importResult && (
          <div className={`mb-4 p-4 rounded-lg ${
            importResult.success 
              ? 'bg-green-50 border border-green-200' 
              : 'bg-red-50 border border-red-200'
          }`}>
            {importResult.success ? (
              <div className="text-green-800">
                <p className="font-semibold">✓ Import Successful!</p>
                <p className="text-sm">Imported: {importResult.imported} codes</p>
                {importResult.skipped > 0 && (
                  <p className="text-sm">Skipped: {importResult.skipped} (already exist)</p>
                )}
                {importResult.errors && importResult.errors.length > 0 && (
                  <details className="mt-2 text-sm">
                    <summary className="cursor-pointer">View errors ({importResult.errors.length})</summary>
                    <ul className="mt-1 ml-4 list-disc">
                      {importResult.errors.map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            ) : (
              <div className="text-red-800">
                <p className="font-semibold">✗ Import Failed</p>
                <p className="text-sm">{importResult.error}</p>
              </div>
            )}
          </div>
        )}
        
        {/* Filters */}
        <div className="bg-white border rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Search Code</label>
              <input
                type="text"
                placeholder="ABC-123..."
                value={filters.code}
                onChange={(e) => setFilters({ ...filters, code: e.target.value, page: 1 })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Rarity</label>
              <select
                value={filters.rarity}
                onChange={(e) => setFilters({ ...filters, rarity: e.target.value, page: 1 })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">All</option>
                <option value="COMMON">Common</option>
                <option value="UNCOMMON">Uncommon</option>
                <option value="RARE">Rare</option>
                <option value="LEGENDARY">Legendary</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Batch</label>
              <select
                value={filters.batch_id}
                onChange={(e) => setFilters({ ...filters, batch_id: e.target.value, page: 1 })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">All</option>
                {batches.map(batch => (
                  <option key={batch.id} value={batch.id}>{batch.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">All</option>
                <option value="UNUSED">Unused</option>
                <option value="PENDING">Pending</option>
                <option value="REDEEMED">Redeemed</option>
                <option value="VOID">Void</option>
              </select>
            </div>
            
            <div className="flex items-end">
              <button
                onClick={() => setFilters({ code: '', rarity: '', batch_id: '', status: '', page: 1, page_size: 50 })}
                className="w-full px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Code</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Rarity</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Batch</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Claimed</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : codes.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    No codes found
                  </td>
                </tr>
              ) : (
                codes.map((code) => (
                  <tr key={code.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        <span className="font-mono font-semibold">{code.code}</span>
                        <button
                          onClick={() => copyToClipboard(code.code)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${getRarityBadge(code.rarity)}`}>
                        {code.rarity}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">{code.batch_name || code.batch_id}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(code.status)}`}>
                        {code.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {code.claimed_email ? (
                        <div>
                          <div>{code.claimed_email}</div>
                          {code.claimed_at && (
                            <div className="text-xs text-gray-500">
                              {new Date(code.claimed_at).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewDetail(code.id)}
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {code.status !== 'VOID' && (
                          <button
                            onClick={() => handleVoidCode(code.id)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                            title="Void Code"
                          >
                            <Ban className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="px-4 py-3 border-t flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {(pagination.page - 1) * pagination.page_size + 1} to {Math.min(pagination.page * pagination.page_size, pagination.total)} of {pagination.total} codes
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                disabled={pagination.page === 1}
                className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 text-sm"
              >
                Previous
              </button>
              <button
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                disabled={pagination.page >= pagination.total_pages}
                className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50 text-sm"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedCode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-bold">Code Details</h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="text-center">
                <div className="text-3xl font-mono font-bold mb-2">{selectedCode.code}</div>
                <div className="flex items-center justify-center space-x-2 mb-4">
                  <span className={`px-3 py-1 text-sm rounded-full ${getRarityBadge(selectedCode.rarity)}`}>
                    {selectedCode.rarity}
                  </span>
                  <span className={`px-3 py-1 text-sm rounded-full ${getStatusBadge(selectedCode.status)}`}>
                    {selectedCode.status}
                  </span>
                </div>
              </div>
              
              {selectedCode.qr_image && (
                <div className="flex justify-center">
                  <img src={selectedCode.qr_image} alt="QR Code" className="w-48 h-48" />
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-600">Batch</div>
                  <div className="font-medium">{selectedCode.batch_name || selectedCode.batch_id}</div>
                </div>
                <div>
                  <div className="text-gray-600">Created</div>
                  <div className="font-medium">{new Date(selectedCode.created_at).toLocaleDateString()}</div>
                </div>
                {selectedCode.claimed_email && (
                  <>
                    <div>
                      <div className="text-gray-600">Claimed By</div>
                      <div className="font-medium">{selectedCode.claimed_email}</div>
                    </div>
                    <div>
                      <div className="text-gray-600">Claimed At</div>
                      <div className="font-medium">{new Date(selectedCode.claimed_at).toLocaleDateString()}</div>
                    </div>
                  </>
                )}
              </div>
              
              <div>
                <div className="text-gray-600 text-sm mb-1">QR URL</div>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={selectedCode.qr_url}
                    readOnly
                    className="flex-1 border rounded px-3 py-2 text-sm bg-gray-50"
                  />
                  <button
                    onClick={() => copyToClipboard(selectedCode.qr_url)}
                    className="p-2 border rounded hover:bg-gray-50"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {selectedCode.events && selectedCode.events.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">Event History</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {selectedCode.events.map((event, idx) => (
                      <div key={idx} className="text-sm border-l-2 border-gray-300 pl-3 py-1">
                        <div className="font-medium">{event.event_type}</div>
                        <div className="text-xs text-gray-500">
                          {new Date(event.created_at).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
